/**
 * Leadership section frontend: tabs, modal, and mobile-only Splide slider.
 */
import { __ } from '@wordpress/i18n';
import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide-core.min.css';
import {
	lockBackgroundScroll,
	unlockBackgroundScroll,
} from '../shared/background-scroll-lock';
import { resetModalScroll } from '../shared/modal-animation';

( function () {
	'use strict';

	const MOBILE_MAX_WIDTH = 767;
	const OPEN_CLASS = 'is-open';
	const SLIDER_ACTIVE_CLASS = 'is-slider-active';
	const VISIBLE_MEMBER_STAGGER_MS = 65;
	const TAB_ANIMATION_DURATION_MS = 420;
	const ARTICLES_SLIDER_MIN_POSTS = 4;
	const ARTICLES_SLIDER_GAP = '28px';
	const ARTICLES_GRID_CLASS =
		'alteia-related-posts__grid alteia-leadership-section__modal-articles-grid';
	let articlesSliderUid = 0;

	/**
	 * Whether a member has a real profile image (not the theme placeholder).
	 *
	 * @param {Object} member Member data.
	 * @return {boolean}
	 */
	function hasMemberProfileImage( member ) {
		const url = member?.imageUrl || member?.image?.url || '';

		if ( ! url || url.includes( 'Placeholder-Image' ) ) {
			return false;
		}

		return true;
	}

	/**
	 * Whether the bio modal should show the member profile image.
	 *
	 * @param {Object} member Member data.
	 * @return {boolean}
	 */
	function shouldShowModalProfileImage( member ) {
		if ( member?.showModalImage === false ) {
			return false;
		}

		return hasMemberProfileImage( member );
	}

	/**
	 * @param {HTMLElement} root
	 * @return {HTMLElement[]}
	 */
	function getFocusableElements( root ) {
		const selectors = [
			'a[href]',
			'button:not([disabled])',
			'textarea:not([disabled])',
			'input:not([disabled])',
			'select:not([disabled])',
			'[tabindex]:not([tabindex="-1"])',
		];

		return Array.from(
			root.querySelectorAll( selectors.join( ',' ) )
		).filter( ( element ) => {
			if ( element.hasAttribute( 'disabled' ) ) {
				return false;
			}

			if ( element.getAttribute( 'aria-hidden' ) ) {
				return false;
			}

			if ( element.closest( '[hidden], [aria-hidden="true"]' ) ) {
				return false;
			}

			return true;
		} );
	}

	/**
	 * @return {boolean}
	 */
	function isMobileViewport() {
		return window.matchMedia( `(max-width: ${ MOBILE_MAX_WIDTH }px)` )
			.matches;
	}

	/**
	 * @param {HTMLElement} section Leadership section root.
	 * @return {Object|null}
	 */
	function readSectionData( section ) {
		const script = section.querySelector(
			'.alteia-leadership-section__data'
		);

		if ( ! script?.textContent ) {
			return null;
		}

		try {
			return JSON.parse( script.textContent );
		} catch {
			return null;
		}
	}

	/**
	 * @param {string} url   LinkedIn profile URL.
	 * @param          value
	 * @return {string}
	 */
	function escapeHtml( value ) {
		return String( value )
			.replace( /&/g, '&amp;' )
			.replace( /</g, '&lt;' )
			.replace( />/g, '&gt;' )
			.replace( /"/g, '&quot;' );
	}

	/**
	 * @param {string} url LinkedIn profile URL.
	 * @return {string}
	 */
	function renderLinkedInIcon( url ) {
		if ( ! url ) {
			return '';
		}

		const safeUrl = escapeHtml( url );

		return `<a class="alteia-leadership-section__modal-social-link" href="${ safeUrl }" target="_blank" rel="noopener noreferrer" aria-label="${ __(
			'LinkedIn profile',
			'alteia'
		) }"><svg xmlns="http://www.w3.org/2000/svg" fill="currentcolor" xml:space="preserve" width="22" height="22" viewBox="0 0 93.06 93.06"><path d="M11.185.08C5.004.08.001 5.092 0 11.259c0 6.173 5.003 11.184 11.186 11.184 6.166 0 11.176-5.011 11.176-11.184C22.362 5.091 17.351.08 11.185.08M1.538 30.926h19.287V92.98H1.538zM69.925 29.383c-9.382 0-15.673 5.144-18.248 10.022h-.258v-8.479H32.92v62.053h19.27V62.281c0-8.093 1.541-15.932 11.575-15.932 9.89 0 10.022 9.256 10.022 16.451v30.178H93.06V58.942c0-16.707-3.605-29.559-23.135-29.559"/></svg></a>`;
	}

	/**
	 * @param {Object} post Formatted post data.
	 * @return {string}
	 */
	function renderModalPostCard( post ) {
		const imageUrl = post?.image?.url || '';
		const imageAlt = escapeHtml( post?.image?.alt || post?.title || '' );
		const category = escapeHtml( post?.category?.name || '' );
		const title = escapeHtml( post?.title || '' );
		const date = escapeHtml( post?.date || '' );
		const link = escapeHtml( post?.link || '#' );

		return `
			<article class="alteia-post-card alteia-related-posts__card">
				<a class="alteia-post-card__link" href="${ link }">
					<div class="alteia-post-card__image${
						imageUrl ? '' : ' alteia-post-card__image--placeholder'
					}">
						${
							imageUrl
								? `<img src="${ escapeHtml(
										imageUrl
								  ) }" alt="${ imageAlt }" loading="lazy" />`
								: '<img src="" alt="" loading="lazy" aria-hidden="true" />'
						}
					</div>
					<div class="alteia-post-card__content">
						<div class="alteia-post-card__content__left">
							${
								category
									? `<div class="alteia-post-card__meta alteia-post-card__meta-category"><span>${ category.toUpperCase() }</span></div>`
									: ''
							}
							${ title ? `<h3 class="alteia-post-card__title">${ title }</h3>` : '' }
						</div>
						<div class="alteia-post-card__content__right">
							${
								date
									? `<div class="alteia-post-card__meta alteia-post-card__meta-date"><span>${ date }</span></div>`
									: ''
							}
							<div class="alteia-post-card__meta alteia-post-card__meta-post-link">
								<span class="alteia-post-card__arrow" aria-hidden="true">
									<svg width="20" height="12" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M20.008 6.00025L14.301 11.7072C13.91 12.0982 13.278 12.0982 12.887 11.7072C12.496 11.3152 12.496 10.6832 12.887 10.2922L16.178 7.00025H1C0.448 7.00025 0 6.55325 0 6.00025C0 5.44725 0.448 5.00025 1 5.00025H16.178L12.887 1.70825C12.496 1.31725 12.496 0.68525 12.887 0.29325C13.278 -0.09775 13.91 -0.09775 14.301 0.29325L20.008 6.00025Z" fill="white"/></svg>
								</span>
								<span class="screen-reader-text">${ __(
									'Read more',
									'alteia'
								) }</span>
							</div>
						</div>
					</div>
				</a>
			</article>
		`;
	}

	/**
	 * Whether related posts should use a Splide slider.
	 *
	 * @param {number} count Related post count.
	 * @return {boolean}
	 */
	function shouldUseArticlesSlider( count ) {
		return count >= ARTICLES_SLIDER_MIN_POSTS;
	}

	/**
	 * @param {'prev'|'next'} direction Arrow direction.
	 * @return {string}
	 */
	function renderArticlesSliderArrow( direction ) {
		const isPrev = direction === 'prev';
		const label = isPrev
			? __( 'Previous featured contributions', 'alteia' )
			: __( 'Next featured contributions', 'alteia' );
		const icon = isPrev
			? `<path d="M6.38001 3.95312L2.33334 7.99979L6.38001 12.0465" stroke="white" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/><path d="M13.6667 8H2.44666" stroke="white" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>`
			: `<path d="M9.62 3.95312L13.6667 7.99979L9.62 12.0465" stroke="white" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/><path d="M2.33334 8H13.5533" stroke="white" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>`;

		return `<button type="button" class="alteia-leadership-section__modal-articles-arrow alteia-leadership-section__modal-articles-arrow--${ direction }" data-leadership-articles-${ direction } aria-label="${ label }"><span aria-hidden="true"><svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">${ icon }</svg></span></button>`;
	}

	/**
	 * Ensure slider chrome exists for older saved modal markup.
	 *
	 * @param {HTMLElement} dialog Modal dialog.
	 * @param {string}      sliderId Unique slider id.
	 * @return {{controls: HTMLElement|null, pagination: HTMLElement|null, header: HTMLElement|null, grid: HTMLElement|null}}
	 */
	function ensureArticlesSliderChrome( dialog, sliderId ) {
		const related = dialog.querySelector(
			'.alteia-leadership-section__modal-related'
		);
		const header = related?.querySelector( '.alteia-related-posts__header' );
		const inner = related?.querySelector( '.alteia-related-posts__inner' );
		const grid = dialog.querySelector(
			'.alteia-leadership-section__modal-articles-grid'
		);

		let controls = dialog.querySelector(
			'[data-leadership-articles-controls]'
		);
		let pagination = dialog.querySelector(
			'[data-leadership-articles-pagination]'
		);

		if ( header && ! controls ) {
			controls = document.createElement( 'div' );
			controls.className =
				'alteia-leadership-section__modal-articles-controls';
			controls.setAttribute( 'data-leadership-articles-controls', '' );
			controls.hidden = true;
			controls.setAttribute( 'aria-hidden', 'true' );
			controls.setAttribute( 'role', 'group' );
			controls.setAttribute(
				'aria-label',
				'Featured contributions slider controls'
			);
			controls.innerHTML =
				renderArticlesSliderArrow( 'prev' ) +
				renderArticlesSliderArrow( 'next' );
			header.appendChild( controls );
		}

		if ( inner && grid && ! pagination ) {
			pagination = document.createElement( 'div' );
			pagination.className =
				'alteia-leadership-section__modal-articles-pagination';
			pagination.setAttribute(
				'data-leadership-articles-pagination',
				''
			);
			pagination.hidden = true;
			pagination.setAttribute( 'aria-hidden', 'true' );
			pagination.setAttribute( 'role', 'group' );
			pagination.setAttribute(
				'aria-label',
				'Featured contributions pagination'
			);
			grid.after( pagination );
		}

		if ( grid && sliderId ) {
			grid.id = sliderId;
		}

		if ( controls && sliderId ) {
			controls
				.querySelectorAll(
					'[data-leadership-articles-prev], [data-leadership-articles-next]'
				)
				.forEach( ( button ) => {
					button.setAttribute( 'aria-controls', sliderId );
				} );
		}

		return { controls, pagination, header, grid };
	}

	/**
	 * Restore the related-posts grid to a flat container.
	 *
	 * @param {HTMLElement} grid Articles grid.
	 */
	function resetArticlesGrid( grid ) {
		if ( ! grid ) {
			return;
		}

		grid.className = ARTICLES_GRID_CLASS;
		grid.removeAttribute( 'style' );
		grid.removeAttribute( 'role' );
		grid.removeAttribute( 'aria-roledescription' );
		grid.removeAttribute( 'aria-label' );
		grid.innerHTML = '';
	}

	/**
	 * Wrap related post cards in Splide markup.
	 *
	 * @param {HTMLElement} grid Articles grid.
	 */
	function buildArticlesSplideMarkup( grid ) {
		if ( ! grid ) {
			return;
		}

		const cards = Array.from( grid.children ).filter( ( item ) =>
			item.classList.contains( 'alteia-related-posts__card' )
		);

		grid.className = `${ ARTICLES_GRID_CLASS } splide`;
		grid.innerHTML = '';

		const track = document.createElement( 'div' );
		track.className = 'splide__track';

		const list = document.createElement( 'ul' );
		list.className = 'splide__list';

		cards.forEach( ( card ) => {
			const slide = document.createElement( 'li' );
			slide.className =
				'splide__slide alteia-leadership-section__modal-articles-slide';
			slide.appendChild( card );
			list.appendChild( slide );
		} );

		track.appendChild( list );
		grid.appendChild( track );
	}

	/**
	 * @param {import('@splidejs/splide').Splide} splide Splide instance.
	 * @return {number}
	 */
	function getArticlesPageCount( splide ) {
		const perPage = Math.max( 1, splide?.options?.perPage || 1 );
		const length = splide?.length || 0;

		return Math.max( 1, Math.ceil( length / perPage ) );
	}

	/**
	 * @param {import('@splidejs/splide').Splide} splide Splide instance.
	 * @param {number|undefined}                  dest   Destination index.
	 * @return {number}
	 */
	function getArticlesActivePage( splide, dest ) {
		const perPage = Math.max( 1, splide?.options?.perPage || 1 );
		const length = Math.max( 1, splide?.length || 1 );
		let index =
			typeof dest === 'number' && ! Number.isNaN( dest )
				? dest
				: splide.index;

		index = ( ( index % length ) + length ) % length;

		const pageCount = getArticlesPageCount( splide );

		return Math.min( Math.floor( index / perPage ), pageCount - 1 );
	}

	/**
	 * @param {HTMLElement} pagination Pagination root.
	 * @param {number}      pageCount  Number of pages.
	 * @param {number}      activePage Active page index.
	 */
	function renderArticlesPagination( pagination, pageCount, activePage ) {
		if ( ! pagination ) {
			return;
		}

		if ( pageCount <= 1 ) {
			pagination.innerHTML = '';
			pagination.hidden = true;
			pagination.setAttribute( 'aria-hidden', 'true' );
			return;
		}

		pagination.hidden = false;
		pagination.setAttribute( 'aria-hidden', 'false' );
		pagination.innerHTML = Array.from( { length: pageCount } )
			.map( ( _, index ) => {
				const isActive = index === activePage;

				return `<button type="button" class="alteia-leadership-section__modal-articles-pagination-dot${
					isActive
						? ' alteia-leadership-section__modal-articles-pagination-dot--active'
						: ''
				}" data-leadership-articles-dot="${ index }" aria-label="Go to page ${
					index + 1
				}" aria-current="${ isActive ? 'true' : 'false' }"></button>`;
			} )
			.join( '' );
	}

	/**
	 * @param {HTMLElement}                           pagination Pagination root.
	 * @param {import('@splidejs/splide').Splide}     splide     Splide instance.
	 * @param {number|undefined}                      dest       Destination index.
	 */
	function syncArticlesPagination( pagination, splide, dest ) {
		if ( ! pagination || ! splide ) {
			return;
		}

		const pageCount = getArticlesPageCount( splide );
		const activePage = getArticlesActivePage( splide, dest );
		const dots = pagination.querySelectorAll(
			'[data-leadership-articles-dot]'
		);

		if ( dots.length !== pageCount ) {
			renderArticlesPagination( pagination, pageCount, activePage );
			return;
		}

		dots.forEach( ( dot ) => {
			const dotIndex = parseInt(
				dot.getAttribute( 'data-leadership-articles-dot' ),
				10
			);
			const isActive = dotIndex === activePage;

			dot.classList.toggle(
				'alteia-leadership-section__modal-articles-pagination-dot--active',
				isActive
			);
			dot.setAttribute( 'aria-current', isActive ? 'true' : 'false' );
		} );
	}

	/**
	 * @param {HTMLElement|null} controls   Arrow controls.
	 * @param {HTMLElement|null} pagination Pagination root.
	 * @param {boolean}          visible    Whether chrome should show.
	 */
	function setArticlesSliderChromeVisibility(
		controls,
		pagination,
		visible
	) {
		if ( controls ) {
			controls.hidden = ! visible;
			controls.setAttribute( 'aria-hidden', visible ? 'false' : 'true' );
		}

		if ( pagination && ! visible ) {
			pagination.hidden = true;
			pagination.setAttribute( 'aria-hidden', 'true' );
			pagination.innerHTML = '';
		}
	}

	/**
	 * @param {HTMLElement} root Slider root.
	 */
	function cleanupArticlesSplideChrome( root ) {
		if ( ! root ) {
			return;
		}

		root.querySelectorAll( '.splide__pagination, .splide__arrows' ).forEach(
			( element ) => element.remove()
		);

		root.querySelectorAll( '.splide__sr' ).forEach( ( element ) => {
			element.setAttribute( 'aria-hidden', 'true' );
		} );
	}

	/**
	 * @param {HTMLElement} section Leadership section root.
	 */
	function initLeadershipSection( section ) {
		const data = readSectionData( section );
		const members = data?.members || [];
		const sectionMode = data?.sectionMode || 'main';
		const departments = Array.isArray( data?.departments )
			? data.departments
			: [];
		const requestedDefaultTab =
			typeof data?.defaultActiveTab === 'string' && data.defaultActiveTab
				? data.defaultActiveTab
				: 'management';
		const defaultActiveTab =
			requestedDefaultTab === 'all' ||
			departments.some(
				( department ) => department?.slug === requestedDefaultTab
			)
				? requestedDefaultTab
				: departments.length
				? 'all'
				: requestedDefaultTab;
		let globalGridColumns = 3;

		if ( data?.gridColumns === 4 || data?.gridColumns === 3 ) {
			globalGridColumns = data.gridColumns;
		} else if (
			section.classList.contains( 'alteia-leadership-section--cols-4' )
		) {
			globalGridColumns = 4;
		}
		const tabGridColumns =
			data?.tabGridColumns &&
			typeof data.tabGridColumns === 'object' &&
			! Array.isArray( data.tabGridColumns )
				? data.tabGridColumns
				: {};
		const modal = section.querySelector(
			'.alteia-leadership-section__modal'
		);
		const relatedArticlesTitle = modal?.querySelector(
			'.alteia-related-posts__title h2'
		);

		// Re-apply gettext at runtime — save.js bakes the locale at edit-save time.
		if ( relatedArticlesTitle ) {
			relatedArticlesTitle.textContent = __(
				'Featured contributions & insights',
				'alteia'
			);
		}

		const dialog = section.querySelector(
			'.alteia-leadership-section__modal-dialog'
		);
		const gridRoot = section.querySelector( '[data-leadership-grid]' );
		const sliderControls = section.querySelector(
			'.alteia-leadership-section__slider-controls'
		);
		const tabButtons = Array.from(
			section.querySelectorAll( '.alteia-leadership-section__tab' )
		);

		const MODAL_BODY_CLASS = 'alteia-leadership-section-modal-open';
		const isSliderEnabled = sectionMode === 'inner';

		/*
		 * Department tab filtering only applies to the inner (tabs) variant.
		 * Main / home curated lists must keep every selected member visible,
		 * including across resize / breakpoint changes.
		 */
		let activeTab = isSliderEnabled ? defaultActiveTab : 'all';
		let splide = null;
		let lastActiveElement = null;
		let desktopGridHTML = '';
		let wasUsingSlider = false;
		let articlesSplide = null;
		let articlesSliderMountTimer = null;
		let modalCloseTimer = null;
		let modalImageRequestId = 0;
		let lastViewportWidth = window.innerWidth;
		const articlesSliderId = `alteia-leadership-articles-slider-${ ++articlesSliderUid }`;
		const articlesWrap = dialog?.querySelector(
			'.alteia-leadership-section__modal-articles'
		);
		const articlesDivider = dialog?.querySelector(
			'.alteia-leadership-section__modal-divider'
		);
		const articlesChrome = dialog
			? ensureArticlesSliderChrome( dialog, articlesSliderId )
			: {
					controls: null,
					pagination: null,
					header: null,
					grid: null,
			  };
		const articlesGrid = articlesChrome.grid;
		const articlesControls = articlesChrome.controls;
		const articlesPagination = articlesChrome.pagination;

		/**
		 * @return {boolean}
		 */
		function shouldUseSlider() {
			return isSliderEnabled && isMobileViewport();
		}

		/**
		 * @return {HTMLElement[]}
		 */
		function getMemberElements() {
			return Array.from(
				section.querySelectorAll( '.alteia-leadership-section__member' )
			);
		}

		/**
		 * Resolve cards-per-row for a tab, falling back to the global setting.
		 *
		 * @param {string} departmentSlug Active department slug.
		 * @return {number} 3 or 4.
		 */
		function resolveTabGridColumns( departmentSlug ) {
			const override = Number( tabGridColumns[ departmentSlug ] );

			return override === 3 || override === 4
				? override
				: globalGridColumns;
		}

		/**
		 * Apply the existing 3/4-column modifier classes for the active tab.
		 *
		 * @param {string} departmentSlug Active department slug.
		 */
		function applyGridLayoutForTab( departmentSlug ) {
			const columns = resolveTabGridColumns( departmentSlug );

			section.classList.remove(
				'alteia-leadership-section--cols-3',
				'alteia-leadership-section--cols-4'
			);
			section.classList.add(
				`alteia-leadership-section--cols-${ columns }`
			);
			section.style.setProperty(
				'--alteia-leadership-section-grid-columns',
				String( columns )
			);
		}

		/**
		 * Toggle member visibility for the active department tab.
		 * No-op for the main / home curated variant.
		 *
		 * @param {string} departmentSlug Active department slug.
		 */
		function updateMemberVisibility( departmentSlug ) {
			if ( ! isSliderEnabled ) {
				return;
			}

			getMemberElements().forEach( ( member ) => {
				if ( member.dataset.siteHidden === 'true' ) {
					member.classList.add(
						'alteia-leadership-section__member--hidden'
					);
					return;
				}

				const departments = ( member.dataset.departments || '' )
					.split( ',' )
					.filter( Boolean );
				const isVisible =
					'all' === departmentSlug ||
					departments.includes( departmentSlug );

				member.classList.toggle(
					'alteia-leadership-section__member--hidden',
					! isVisible
				);
			} );
		}

		let tabAnimationTimer = null;

		/**
		 * Assign stagger delays based on visible cards only.
		 */
		function applyVisibleMemberAnimationDelays() {
			getMemberElements().forEach( ( member ) => {
				member.style.removeProperty(
					'--alteia-leadership-reveal-delay'
				);
			} );

			if ( ! gridRoot ) {
				return;
			}

			const visibleMembers = getMemberElements().filter(
				( member ) =>
					! member.classList.contains(
						'alteia-leadership-section__member--hidden'
					)
			);

			visibleMembers.forEach( ( member, visibleIndex ) => {
				member.style.setProperty(
					'--alteia-leadership-reveal-delay',
					`${ visibleIndex * VISIBLE_MEMBER_STAGGER_MS }ms`
				);
			} );
		}

		/**
		 * Replay the inner-mode card reveal animation after a tab change.
		 */
		function triggerTabRevealAnimation() {
			if ( ! isSliderEnabled || ! gridRoot ) {
				return;
			}

			gridRoot.classList.remove( 'is-tab-animating' );
			void gridRoot.offsetWidth;
			gridRoot.classList.add( 'is-tab-animating' );

			if ( tabAnimationTimer ) {
				window.clearTimeout( tabAnimationTimer );
			}

			tabAnimationTimer = window.setTimeout( () => {
				gridRoot.classList.remove( 'is-tab-animating' );
				tabAnimationTimer = null;
			}, TAB_ANIMATION_DURATION_MS );
		}

		/**
		 * @param {string}  departmentSlug  Active department slug.
		 * @param {Object}  options            Filter options.
		 * @param {boolean} options.animate    Whether to replay the tab reveal animation.
		 * @param {boolean} options.syncSlider Whether to rebuild the mobile slider now.
		 */
		function applyTabFilter( departmentSlug, options = {} ) {
			const { animate = true, syncSlider = true } = options;
			activeTab = departmentSlug;

			tabButtons.forEach( ( button ) => {
				const slug = button.dataset.department || 'all';
				const isActive = slug === departmentSlug;
				button.classList.toggle(
					'alteia-leadership-section__tab--active',
					isActive
				);
				button.setAttribute(
					'aria-selected',
					isActive ? 'true' : 'false'
				);
			} );

			applyGridLayoutForTab( departmentSlug );
			updateMemberVisibility( departmentSlug );
			applyVisibleMemberAnimationDelays();

			/*
			 * Desktop tab cards use a reveal animation. On the mobile Splide track,
			 * member transform animations can fight Splide's translateX (Safari).
			 */
			if ( animate && ! shouldUseSlider() ) {
				triggerTabRevealAnimation();
			}

			/*
			 * Always rebuild from the flat desktop snapshot + active-tab filter.
			 * Restoring the snapshot alone is not enough — tab visibility must be
			 * re-applied after restore or the previous tab's members stay in the slider.
			 */
			if ( syncSlider && shouldUseSlider() ) {
				rebuildSlider();
			}
		}

		/**
		 * Ensure the grid is in its original flat markup before slider work.
		 */
		function ensureFlatGrid() {
			if ( ! gridRoot ) {
				return;
			}

			if ( ! desktopGridHTML ) {
				snapshotDesktopGrid();
			}

			if ( gridRoot.querySelector( '.splide__track' ) ) {
				restoreDesktopGrid();
			}
		}

		/**
		 * Restore the desktop grid markup and re-apply the active department filter.
		 * Tab visibility cannot be trusted from the snapshot (PHP may bake in the
		 * default tab's --hidden classes).
		 */
		function prepareFlatGridForActiveTab() {
			ensureFlatGrid();
			updateMemberVisibility( activeTab );
			applyVisibleMemberAnimationDelays();
		}

		/**
		 * Measure the tallest visible member card in the flat grid or active slider.
		 *
		 * @return {number}
		 */
		function measureMemberCardsHeight() {
			if ( ! gridRoot ) {
				return 0;
			}

			const members = Array.from(
				gridRoot.querySelectorAll(
					'.alteia-leadership-section__member'
				)
			).filter(
				( member ) =>
					! member.classList.contains(
						'alteia-leadership-section__member--hidden'
					)
			);

			let maxHeight = 0;

			members.forEach( ( member ) => {
				maxHeight = Math.max(
					maxHeight,
					member.scrollHeight,
					member.offsetHeight,
					member.getBoundingClientRect().height
				);
			} );

			return maxHeight;
		}

		/**
		 * Apply a stable min-height for the slider track.
		 *
		 * @return {void}
		 */
		function applySliderTrackHeight() {
			const measured = measureMemberCardsHeight();

			if ( measured <= 0 ) {
				resetSliderHeight();
				return;
			}

			const trackHeight = `${ Math.ceil( measured + 12 ) }px`;
			section.style.setProperty(
				'--alteia-leadership-slider-height',
				trackHeight
			);
		}

		/**
		 * Remove Splide UI we do not use and can affect layout.
		 */
		function cleanupSplideChrome() {
			if ( ! gridRoot ) {
				return;
			}

			gridRoot
				.querySelectorAll( '.splide__pagination' )
				.forEach( ( element ) => element.remove() );
		}

		/**
		 * Clear slider layout styles applied during init.
		 */
		function resetSliderHeight() {
			section.style.removeProperty( '--alteia-leadership-slider-height' );
		}

		/**
		 * Restore the original desktop grid markup.
		 */
		function restoreDesktopGrid() {
			if ( ! gridRoot || ! desktopGridHTML ) {
				return;
			}

			resetSliderHeight();
			gridRoot.innerHTML = desktopGridHTML;
			gridRoot.className = 'alteia-leadership-section__grid';
			gridRoot.setAttribute( 'data-leadership-grid', '' );
		}

		/**
		 * Snapshot the desktop grid before Splide transforms it.
		 */
		function snapshotDesktopGrid() {
			if ( ! gridRoot || desktopGridHTML ) {
				return;
			}

			desktopGridHTML = gridRoot.innerHTML;
		}

		/**
		 * Build Splide markup with one member card per slide.
		 */
		function buildSplideMarkup() {
			if ( ! gridRoot ) {
				return;
			}

			const items = Array.from( gridRoot.children ).filter(
				( item ) =>
					item.classList.contains(
						'alteia-leadership-section__member'
					) &&
					! item.classList.contains(
						'alteia-leadership-section__member--hidden'
					)
			);

			gridRoot.className =
				'alteia-leadership-section__grid alteia-leadership-section__grid--slider splide';
			gridRoot.innerHTML = '';

			const track = document.createElement( 'div' );
			track.className = 'splide__track';

			const list = document.createElement( 'ul' );
			list.className = 'splide__list';

			items.forEach( ( item ) => {
				const slide = document.createElement( 'li' );
				slide.className =
					'splide__slide alteia-leadership-section__grid-slide';
				slide.appendChild( item );
				list.appendChild( slide );
			} );

			track.appendChild( list );
			gridRoot.appendChild( track );
		}

		/**
		 * Destroy Splide and restore desktop grid.
		 *
		 * @param {Object}  options                  Destroy options.
		 * @param {boolean} options.reapplyTabFilter Whether to re-run department filtering.
		 */
		function destroySlider( options = {} ) {
			const { reapplyTabFilter = true } = options;

			if ( splide ) {
				splide.destroy();
				splide = null;
			}

			restoreDesktopGrid();
			section.classList.remove( SLIDER_ACTIVE_CLASS );
			wasUsingSlider = false;

			if ( sliderControls ) {
				sliderControls.hidden = true;
				sliderControls.setAttribute( 'aria-hidden', 'true' );
			}

			if ( reapplyTabFilter ) {
				updateMemberVisibility( activeTab );
			}
		}

		/**
		 * Refresh Splide layout after the track has a measurable width.
		 * Safari often needs a post-mount refresh after tab/DOM rebuilds.
		 */
		function refreshMountedSlider() {
			if ( ! splide || ! shouldUseSlider() ) {
				return;
			}

			window.requestAnimationFrame( () => {
				if ( ! splide || ! shouldUseSlider() ) {
					return;
				}

				splide.refresh();
				cleanupSplideChrome();
				applySliderTrackHeight();
			} );
		}

		/**
		 * Initialize Splide on inner-mode mobile viewports only.
		 * Safe to call repeatedly: an already-mounted mobile instance is kept
		 * in place so the current slide index is preserved.
		 */
		function initSlider() {
			if ( ! gridRoot || ! isSliderEnabled ) {
				return;
			}

			if ( ! shouldUseSlider() ) {
				if (
					wasUsingSlider ||
					section.classList.contains( SLIDER_ACTIVE_CLASS )
				) {
					destroySlider();
				}
				return;
			}

			/*
			 * Keep a stable lifecycle on mobile. iOS Safari fires resize when the
			 * address bar shows/hides (height-only); remounting would jump to slide 1.
			 * Refresh only when the viewport width actually changed (e.g. rotate).
			 */
			if ( splide && wasUsingSlider ) {
				const width = window.innerWidth;

				if ( width !== lastViewportWidth ) {
					lastViewportWidth = width;
					refreshMountedSlider();
				}

				return;
			}

			/*
			 * Restore the flat member grid, then filter for the active tab before
			 * wrapping slides. Never build from the snapshot's baked-in --hidden state.
			 */
			prepareFlatGridForActiveTab();
			applySliderTrackHeight();
			buildSplideMarkup();

			const visibleSlides = gridRoot.querySelectorAll(
				'.alteia-leadership-section__grid-slide'
			).length;

			if ( visibleSlides <= 0 ) {
				destroySlider();
				return;
			}

			section.classList.add( SLIDER_ACTIVE_CLASS );
			wasUsingSlider = true;
			lastViewportWidth = window.innerWidth;

			if ( sliderControls ) {
				const showControls = visibleSlides > 1;
				sliderControls.hidden = ! showControls;
				sliderControls.setAttribute(
					'aria-hidden',
					showControls ? 'false' : 'true'
				);
			}

			splide = new Splide( gridRoot, {
				type: 'slide',
				direction:
					document.documentElement.getAttribute( 'dir' ) === 'rtl'
						? 'rtl'
						: 'ltr',
				perPage: 1,
				perMove: 1,
				gap: 0,
				padding: 0,
				pagination: false,
				arrows: false,
				drag: visibleSlides > 1,
				speed: 400,
				rewind: false,
				autoHeight: false,
				/*
				 * waitForTransition can leave Safari stuck on the first slide when
				 * a transitionend event is missed after a tab rebuild.
				 */
				waitForTransition: false,
				updateOnMove: true,
			} );

			splide.on( 'mounted', () => {
				cleanupSplideChrome();
				applySliderTrackHeight();
			} );

			splide.on( 'move', () => {
				gridRoot.classList.add(
					'alteia-leadership-section__grid--is-moving'
				);
			} );

			splide.on( 'moved', () => {
				gridRoot.classList.remove(
					'alteia-leadership-section__grid--is-moving'
				);
			} );

			splide.mount();
			cleanupSplideChrome();
			// Fresh mounts start at index 0 — do not call go(0) after refresh paths.
			refreshMountedSlider();
		}

		/**
		 * Rebuild the mobile slider after tab changes (intentional remount → slide 1).
		 */
		function rebuildSlider() {
			if ( ! shouldUseSlider() ) {
				destroySlider();
				return;
			}

			/*
			 * Tear down without filtering — initSlider re-applies the active tab
			 * after restore so slides always match the current department.
			 */
			destroySlider( { reapplyTabFilter: false } );
			initSlider();
		}

		/**
		 * Sync slider presence to the current viewport.
		 * Destroy/remount only when crossing the mobile breakpoint.
		 */
		function syncSliderToViewport() {
			if ( ! isSliderEnabled ) {
				return;
			}

			const wantsSlider = shouldUseSlider();

			if ( wantsSlider ) {
				initSlider();
				return;
			}

			lastViewportWidth = window.innerWidth;

			if (
				wasUsingSlider ||
				section.classList.contains( SLIDER_ACTIVE_CLASS )
			) {
				destroySlider();
			}
		}

		/**
		 * Tear down the modal related-posts slider without touching the member grid slider.
		 */
		function destroyArticlesSlider() {
			window.clearTimeout( articlesSliderMountTimer );
			articlesSliderMountTimer = null;

			if ( articlesSplide ) {
				articlesSplide.destroy();
				articlesSplide = null;
			}

			resetArticlesGrid( articlesGrid );
			articlesWrap?.classList.remove( 'is-articles-slider' );
			setArticlesSliderChromeVisibility(
				articlesControls,
				articlesPagination,
				false
			);
		}

		/**
		 * Move the related-posts slider by one page, wrapping infinitely.
		 *
		 * @param {'<'|'>'} direction Splide direction token.
		 */
		function goArticlesDirection( direction ) {
			if ( ! articlesSplide ) {
				return;
			}

			const pageCount = getArticlesPageCount( articlesSplide );
			const current = getArticlesActivePage( articlesSplide );
			const delta = direction === '>' ? 1 : -1;
			const nextPage = ( current + delta + pageCount ) % pageCount;
			const perPage = Math.max( 1, articlesSplide.options.perPage || 1 );
			const length = articlesSplide.length || 0;
			const target = Math.min(
				nextPage * perPage,
				Math.max( 0, length - 1 )
			);

			articlesSplide.go( target );
		}

		/**
		 * Mount Splide on the related-posts grid after the modal is visible.
		 */
		function mountArticlesSlider() {
			if (
				! articlesGrid ||
				! modal?.classList.contains( OPEN_CLASS ) ||
				! articlesGrid.querySelector( '.splide__list' )
			) {
				return;
			}

			if ( articlesSplide ) {
				articlesSplide.destroy();
				articlesSplide = null;
			}

			articlesSplide = new Splide( articlesGrid, {
				type: 'loop',
				direction:
					document.documentElement.getAttribute( 'dir' ) === 'rtl'
						? 'rtl'
						: 'ltr',
				perPage: 3,
				perMove: 1,
				gap: ARTICLES_SLIDER_GAP,
				padding: 0,
				pagination: false,
				arrows: false,
				drag: true,
				keyboard: 'focused',
				speed: 400,
				easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
				autoHeight: false,
				waitForTransition: true,
				flickPower: 300,
				flickMaxPages: 1,
				updateOnMove: true,
				reduceMotion: {
					speed: 0,
					rewindSpeed: 0,
					autoplay: false,
				},
				breakpoints: {
					1024: {
						perPage: 2,
					},
					480: {
						perPage: 1,
					},
				},
				i18n: {
					prev: 'Previous featured contributions',
					next: 'Next featured contributions',
					slideX: 'Go to slide %s',
					pageX: 'Go to page %s',
				},
			} );

			const syncPagination = ( dest ) => {
				syncArticlesPagination(
					articlesPagination,
					articlesSplide,
					dest
				);
			};

			articlesSplide.on( 'mounted', () => {
				cleanupArticlesSplideChrome( articlesGrid );
				syncPagination();
			} );

			articlesSplide.on( 'move', ( _index, _prev, dest ) => {
				syncPagination( dest );
			} );

			articlesSplide.on( 'moved', ( index ) => {
				syncPagination( index );
			} );

			articlesSplide.on( 'updated', () => {
				cleanupArticlesSplideChrome( articlesGrid );
				syncPagination();
			} );

			articlesSplide.mount();
			cleanupArticlesSplideChrome( articlesGrid );
			syncPagination();
		}

		/**
		 * Initialize or skip the related-posts slider for the current member.
		 *
		 * @param {Array<Object>} articles Related posts.
		 */
		function renderModalArticles( articles ) {
			destroyArticlesSlider();

			if ( ! articlesWrap || ! articlesGrid ) {
				return;
			}

			if ( ! articles.length ) {
				articlesWrap.hidden = true;
				if ( articlesDivider ) {
					articlesDivider.hidden = true;
				}
				return;
			}

			articlesWrap.hidden = false;
			if ( articlesDivider ) {
				articlesDivider.hidden = false;
			}

			articlesGrid.innerHTML = articles
				.map( ( post ) => renderModalPostCard( post ) )
				.join( '' );

			if ( ! shouldUseArticlesSlider( articles.length ) ) {
				return;
			}

			articlesWrap.classList.add( 'is-articles-slider' );
			buildArticlesSplideMarkup( articlesGrid );
			setArticlesSliderChromeVisibility(
				articlesControls,
				articlesPagination,
				true
			);
		}

		/**
		 * Mount the related-posts slider after the modal has a measurable width.
		 */
		function scheduleArticlesSliderMount() {
			window.clearTimeout( articlesSliderMountTimer );

			if ( ! articlesWrap?.classList.contains( 'is-articles-slider' ) ) {
				return;
			}

			window.requestAnimationFrame( () => {
				window.requestAnimationFrame( () => {
					mountArticlesSlider();
					articlesSliderMountTimer = window.setTimeout( () => {
						if (
							articlesSplide &&
							modal?.classList.contains( OPEN_CLASS )
						) {
							articlesSplide.refresh();
							syncArticlesPagination(
								articlesPagination,
								articlesSplide
							);
						}
					}, 260 );
				} );
			} );
		}

		/**
		 * Hide the profile image immediately, then reveal it once the new src is ready.
		 *
		 * @param {HTMLImageElement|null} image Profile image element.
		 * @param {string}                url   Image URL.
		 * @param {string}                alt   Image alt text.
		 */
		function setModalProfileImage( image, url, alt ) {
			const requestId = ++modalImageRequestId;

			if ( ! image ) {
				return;
			}

			image.loading = 'eager';
			image.decoding = 'async';
			image.classList.add( 'is-loading' );
			image.alt = alt || '';

			const reveal = () => {
				if ( requestId !== modalImageRequestId ) {
					return;
				}

				image.classList.remove( 'is-loading' );
			};

			if ( ! url ) {
				image.removeAttribute( 'src' );
				reveal();
				return;
			}

			if (
				image.getAttribute( 'src' ) === url &&
				image.complete &&
				image.naturalWidth > 0
			) {
				reveal();
				return;
			}

			image.src = url;

			if ( image.complete && image.naturalWidth > 0 ) {
				reveal();
				return;
			}

			if ( typeof image.decode === 'function' ) {
				image.decode().then( reveal ).catch( reveal );
				return;
			}

			image.addEventListener( 'load', reveal, { once: true } );
			image.addEventListener( 'error', reveal, { once: true } );
		}

		/**
		 * Drop the previous profile image so it cannot flash on the next open.
		 *
		 * @param {HTMLImageElement|null} image Profile image element.
		 */
		function resetModalProfileImage( image ) {
			modalImageRequestId += 1;

			if ( ! image ) {
				return;
			}

			image.classList.add( 'is-loading' );
			image.removeAttribute( 'src' );
			image.alt = '';
		}

		/**
		 * @param {Object} member Member data.
		 */
		function openModal( member ) {
			if ( ! modal || ! dialog ) {
				return;
			}

			lastActiveElement = document.activeElement;

			const image = dialog.querySelector(
				'.alteia-leadership-section__modal-image'
			);
			const modalMedia = dialog.querySelector(
				'.alteia-leadership-section__modal-media'
			);
			const name = dialog.querySelector(
				'.alteia-leadership-section__modal-name'
			);
			const role = dialog.querySelector(
				'.alteia-leadership-section__modal-role'
			);
			const bio = dialog.querySelector(
				'.alteia-leadership-section__modal-bio'
			);
			const social = dialog.querySelector(
				'.alteia-leadership-section__modal-social'
			);

			const hasProfileImage = shouldShowModalProfileImage( member );

			dialog.classList.toggle(
				'alteia-leadership-section__modal-dialog--no-image',
				! hasProfileImage
			);

			if ( modalMedia ) {
				modalMedia.hidden = ! hasProfileImage;
			}

			setModalProfileImage(
				image,
				hasProfileImage
					? member.imageUrl || member.image?.url || ''
					: '',
				hasProfileImage ? member.imageAlt || member.name || '' : ''
			);

			if ( name ) {
				name.textContent = member.name || '';
			}

			if ( role ) {
				role.textContent = member.role || member.jobTitle || '';
			}

			if ( bio ) {
				bio.innerHTML = member.bio || member.bioPlain || '';
			}

			if ( social ) {
				social.innerHTML = renderLinkedInIcon( member.linkedin || '' );
			}

			window.clearTimeout( modalCloseTimer );
			renderModalArticles(
				Array.isArray( member.articles ) ? member.articles : []
			);

			modal.hidden = false;
			modal.classList.add( OPEN_CLASS );
			modal.setAttribute( 'aria-hidden', 'false' );
			lockBackgroundScroll( {
				bodyClass: MODAL_BODY_CLASS,
				compensateScrollbar: true,
			} );
			resetModalScroll( modal );
			resetModalScroll( dialog );

			scheduleArticlesSliderMount();

			const closeButton = dialog.querySelector(
				'.alteia-leadership-section__modal-close'
			);
			if ( closeButton ) {
				closeButton.focus();
			}
		}

		/**
		 * Close the modal.
		 */
		function closeModal() {
			if ( ! modal ) {
				return;
			}

			window.clearTimeout( articlesSliderMountTimer );
			articlesSliderMountTimer = null;

			modal.classList.remove( OPEN_CLASS );
			modal.setAttribute( 'aria-hidden', 'true' );
			unlockBackgroundScroll( { bodyClass: MODAL_BODY_CLASS } );

			window.clearTimeout( modalCloseTimer );
			modalCloseTimer = window.setTimeout( () => {
				modal.hidden = true;
				destroyArticlesSlider();
				resetModalProfileImage(
					dialog?.querySelector(
						'.alteia-leadership-section__modal-image'
					)
				);
				if (
					lastActiveElement &&
					typeof lastActiveElement.focus === 'function'
				) {
					lastActiveElement.focus();
				}
			}, 200 );
		}

		if ( sectionMode === 'inner' && tabButtons.length ) {
			const tablist = section.querySelector(
				'.alteia-leadership-section__tabs-track'
			);

			/*
			 * Snapshot the member grid once before JS tab filtering mutates
			 * --hidden classes. Slider rebuilds restore from this snapshot, then
			 * re-apply the active tab via prepareFlatGridForActiveTab().
			 */
			snapshotDesktopGrid();

			/*
			 * Apply the default tab without rebuilding the slider yet — the shared
			 * initSlider() call below mounts once and avoids a double destroy/mount
			 * that can leave Safari stuck on the first slide.
			 */
			applyTabFilter( defaultActiveTab, {
				animate: false,
				syncSlider: false,
			} );

			tabButtons.forEach( ( button ) => {
				const slug = button.dataset.department || 'all';
				button.setAttribute(
					'tabindex',
					slug === defaultActiveTab ? '0' : '-1'
				);

				button.addEventListener( 'click', () => {
					const nextSlug = button.dataset.department || 'all';
					applyTabFilter( nextSlug );
					tabButtons.forEach( ( tab ) => {
						tab.setAttribute(
							'tabindex',
							tab === button ? '0' : '-1'
						);
					} );
				} );
			} );

			tablist?.addEventListener( 'keydown', ( event ) => {
				const currentIndex = tabButtons.findIndex(
					( button ) => button === document.activeElement
				);

				if ( currentIndex === -1 ) {
					return;
				}

				let nextIndex = currentIndex;

				if ( event.key === 'ArrowRight' ) {
					nextIndex = ( currentIndex + 1 ) % tabButtons.length;
				} else if ( event.key === 'ArrowLeft' ) {
					nextIndex =
						( currentIndex - 1 + tabButtons.length ) %
						tabButtons.length;
				} else if ( event.key === 'Home' ) {
					nextIndex = 0;
				} else if ( event.key === 'End' ) {
					nextIndex = tabButtons.length - 1;
				} else {
					return;
				}

				event.preventDefault();
				const nextButton = tabButtons[ nextIndex ];
				nextButton.focus();
				tabButtons.forEach( ( tab, tabIndex ) => {
					tab.setAttribute(
						'tabindex',
						tabIndex === nextIndex ? '0' : '-1'
					);
				} );
				applyTabFilter( nextButton.dataset.department || 'all' );
			} );
		}

		section.addEventListener( 'click', ( event ) => {
			const memberButton = event.target.closest(
				'.alteia-leadership-section__member'
			);

			if ( ! memberButton || ! section.contains( memberButton ) ) {
				return;
			}

			if (
				memberButton.classList.contains(
					'alteia-leadership-section__member--static'
				) ||
				memberButton.dataset.disableModal === 'true'
			) {
				return;
			}

			const index = Number( memberButton.dataset.memberIndex );
			const member = members[ index ];

			if ( member && ! member.isSiteHidden && ! member.disableModal ) {
				openModal( member );
			}
		} );

		if ( sliderControls ) {
			sliderControls.addEventListener( 'click', ( event ) => {
				if ( ! splide || ! shouldUseSlider() ) {
					return;
				}

				if ( event.target.closest( '[data-leadership-slider-prev]' ) ) {
					splide.go( '<' );
				}

				if ( event.target.closest( '[data-leadership-slider-next]' ) ) {
					splide.go( '>' );
				}
			} );
		}

		if ( articlesControls ) {
			articlesControls.addEventListener( 'click', ( event ) => {
				if ( ! articlesSplide ) {
					return;
				}

				if (
					event.target.closest(
						'[data-leadership-articles-prev]'
					)
				) {
					event.preventDefault();
					goArticlesDirection( '<' );
					return;
				}

				if (
					event.target.closest(
						'[data-leadership-articles-next]'
					)
				) {
					event.preventDefault();
					goArticlesDirection( '>' );
				}
			} );
		}

		if ( articlesPagination ) {
			articlesPagination.addEventListener( 'click', ( event ) => {
				if ( ! articlesSplide ) {
					return;
				}

				const dotButton = event.target.closest(
					'[data-leadership-articles-dot]'
				);

				if ( ! dotButton ) {
					return;
				}

				event.preventDefault();
				const targetPage = parseInt(
					dotButton.getAttribute( 'data-leadership-articles-dot' ),
					10
				);

				if ( Number.isNaN( targetPage ) ) {
					return;
				}

				const perPage = Math.max(
					1,
					articlesSplide.options.perPage || 1
				);
				const length = articlesSplide.length || 0;
				const target = Math.min(
					targetPage * perPage,
					Math.max( 0, length - 1 )
				);

				articlesSplide.go( target );
			} );
		}

		if ( modal ) {
			modal
				.querySelectorAll( '[data-leadership-modal-close]' )
				.forEach( ( closeTrigger ) => {
					closeTrigger.addEventListener( 'click', closeModal );
				} );

			modal.addEventListener( 'transitionend', ( event ) => {
				if (
					event.propertyName !== 'opacity' ||
					event.target !== modal ||
					! articlesSplide ||
					! modal.classList.contains( OPEN_CLASS )
				) {
					return;
				}

				articlesSplide.refresh();
				syncArticlesPagination( articlesPagination, articlesSplide );
			} );
		}

		document.addEventListener( 'keydown', ( event ) => {
			if ( ! modal?.classList.contains( OPEN_CLASS ) ) {
				return;
			}

			if ( event.key === 'Escape' ) {
				event.preventDefault();
				closeModal();
				return;
			}

			if ( event.key === 'Tab' && dialog ) {
				const focusables = getFocusableElements( dialog );
				if ( focusables.length === 0 ) {
					return;
				}

				const first = focusables[ 0 ];
				const last = focusables[ focusables.length - 1 ];
				const active = document.activeElement;

				if ( event.shiftKey && active === first ) {
					event.preventDefault();
					last.focus();
				} else if ( ! event.shiftKey && active === last ) {
					event.preventDefault();
					first.focus();
				}
			}
		} );

		initSlider();

		let resizeTimer;
		window.addEventListener( 'resize', () => {
			window.clearTimeout( resizeTimer );
			resizeTimer = window.setTimeout( () => {
				if (
					articlesSplide &&
					modal?.classList.contains( OPEN_CLASS )
				) {
					articlesSplide.refresh();
					syncArticlesPagination(
						articlesPagination,
						articlesSplide
					);
				}

				/*
				 * Do not rebuild on every resize. Mobile Safari fires resize during
				 * scroll (URL bar) and touch; remounting reset the active slide to 0.
				 * syncSliderToViewport() only remounts when crossing the breakpoint.
				 */
				syncSliderToViewport();
			}, 150 );
		} );
	}

	document.addEventListener( 'DOMContentLoaded', () => {
		document
			.querySelectorAll( '.alteia-leadership-section' )
			.forEach( initLeadershipSection );
	} );
} )();
